// packages/api-client/src/services/widgetService.ts
import axios from 'axios';
import { getApiClient, getRawApiBaseUrl } from '../client';
import { handleApiError } from '../utils/handleApiError';
import type {
  ApiErrorResponse,
  WidgetTemplateResponse,
  WidgetTemplateCreatePayload,
  WidgetTemplateUpdatePayload,
  AllowedOriginResponseItem,
  WidgetVersionResponse,
  WidgetVersionCreatePayload,
  WidgetVersionUpdatePayload,
  WidgetEmbedCodeResponse,
  WidgetPreviewTokenResponse,
  WidgetTemplateConfigSchemaResponse,
  WidgetTemplateConfigSchemaUpdatePayload,
  WidgetVersionStatusResponse,
  WidgetTemplateDisablePayload,
  WidgetTemplateEmbeddedPayload,
  ServiceResponse,
  UUID,
} from '@dopomogai/types';

export const normalizeUuid = (value?: string | null): string => {
  if (!value) return '';
  return value.replace(/^urn:uuid:/i, '').replace(/^uuid:/i, '').trim();
};

const buildMissingIdError = (fieldName: string): ApiErrorResponse =>
  ({
    isError: true,
    message: `${fieldName} is required.`,
    status: 400,
    originalError: new Error(`${fieldName} is required.`),
  }) as ApiErrorResponse;

export const widgetService = {
  // ---------------------------------------------------------------------------
  // Templates
  // ---------------------------------------------------------------------------
  listTemplates: (): ServiceResponse<WidgetTemplateResponse[]> => {
    return getApiClient()
      .get<WidgetTemplateResponse[]>('/widgets/templates')
      .then((res) => res.data)
      .catch((err) => handleApiError(err, 'listing widget templates'));
  },

  getTemplate: (templateId: UUID): ServiceResponse<WidgetTemplateResponse> => {
    const cleanId = normalizeUuid(templateId);
    return getApiClient()
      .get<WidgetTemplateResponse>(`/widgets/templates/${cleanId}`)
      .then((res) => res.data)
      .catch((err) => handleApiError(err, `fetching template ${cleanId}`));
  },

  createTemplate: (
    payload: WidgetTemplateCreatePayload
  ): ServiceResponse<WidgetTemplateResponse> => {
    return getApiClient()
      .post<WidgetTemplateResponse>('/widgets/templates', payload)
      .then((res) => res.data)
      .catch((err) => handleApiError(err, 'creating widget template'));
  },

  updateTemplate: (
    templateId: UUID,
    payload: WidgetTemplateUpdatePayload
  ): ServiceResponse<WidgetTemplateResponse> => {
    const cleanId = normalizeUuid(templateId);
    return getApiClient()
      .put<WidgetTemplateResponse>(`/widgets/templates/${cleanId}`, payload)
      .then((res) => res.data)
      .catch((err) => handleApiError(err, `updating template ${cleanId}`));
  },

  disableTemplate: (
    templateId: UUID,
    payload?: WidgetTemplateDisablePayload
  ): ServiceResponse<WidgetTemplateResponse> => {
    const cleanId = normalizeUuid(templateId);
    return getApiClient()
      .post<WidgetTemplateResponse>(`/widgets/templates/${cleanId}/disable`, payload ?? {})
      .then((res) => res.data)
      .catch((err) => handleApiError(err, `disabling template ${cleanId}`));
  },

  enableTemplate: (templateId: UUID): ServiceResponse<WidgetTemplateResponse> => {
    const cleanId = normalizeUuid(templateId);
    return getApiClient()
      .post<WidgetTemplateResponse>(`/widgets/templates/${cleanId}/enable`)
      .then((res) => res.data)
      .catch((err) => handleApiError(err, `enabling template ${cleanId}`));
  },

  recordTemplateEmbedded: (
    templateId: UUID,
    payload?: WidgetTemplateEmbeddedPayload
  ): ServiceResponse<{ status: 'success' }> => {
    const cleanId = normalizeUuid(templateId);
    return getApiClient()
      .post(`/widgets/templates/${cleanId}/embedded`, payload ?? {})
      .then(() => ({ status: 'success' } as const))
      .catch((err) =>
        handleApiError(err, `recording embedded for template ${cleanId}`)
      );
  },

  recordTemplateEmbeddedPublic: (
    templateId: UUID,
    payload?: WidgetTemplateEmbeddedPayload
  ): ServiceResponse<{ status: 'success' }> => {
    const cleanId = normalizeUuid(templateId);
    return getApiClient()
      .post(`/widgets/templates/${cleanId}/embedded-public`, payload ?? {})
      .then(() => ({ status: 'success' } as const))
      .catch((err) =>
        handleApiError(err, `recording embedded (public) for template ${cleanId}`)
      );
  },

  deleteTemplate: (templateId: UUID): ServiceResponse<{ status: 'success' }> => {
    const cleanId = normalizeUuid(templateId);
    return getApiClient()
      .delete(`/widgets/templates/${cleanId}`)
      .then(() => ({ status: 'success' } as const))
      .catch((err) => handleApiError(err, `deleting template ${cleanId}`));
  },

  getTemplateAllowedOrigins: (
    templateId: UUID
  ): ServiceResponse<AllowedOriginResponseItem[]> => {
    const cleanId = normalizeUuid(templateId);
    if (!cleanId) return Promise.resolve(buildMissingIdError('templateId'));
    return getApiClient()
      .get<AllowedOriginResponseItem[]>(`/widgets/templates/${cleanId}/allowed-origins`)
      .then((res) => res.data)
      .catch((err) =>
        handleApiError(err, `fetching allowed origins for template ${cleanId}`)
      );
  },

  updateTemplateAllowedOrigins: (
    templateId: UUID,
    originsList: string[]
  ): ServiceResponse<AllowedOriginResponseItem[]> => {
    const cleanId = normalizeUuid(templateId);
    if (!cleanId) return Promise.resolve(buildMissingIdError('templateId'));
    return getApiClient()
      .put<AllowedOriginResponseItem[]>(
        `/widgets/templates/${cleanId}/allowed-origins`,
        originsList
      )
      .then((res) => res.data)
      .catch((err) =>
        handleApiError(err, `updating allowed origins for template ${cleanId}`)
      );
  },

  // ---------------------------------------------------------------------------
  // Versions
  // ---------------------------------------------------------------------------
  listVersionsByTemplate: (
    templateId: UUID
  ): ServiceResponse<WidgetVersionResponse[]> => {
    const cleanId = normalizeUuid(templateId);
    if (!cleanId) return Promise.resolve(buildMissingIdError('templateId'));
    return getApiClient()
      .get<WidgetVersionResponse[]>(`/widgets/versions/by_template/${cleanId}`)
      .then((res) => res.data)
      .catch((err) =>
        handleApiError(err, `listing versions for template ${cleanId}`)
      );
  },

  getVersionsByAgent: (agentId: UUID): ServiceResponse<WidgetVersionResponse[]> => {
    const cleanAgentId = normalizeUuid(agentId);
    if (!cleanAgentId) return Promise.resolve(buildMissingIdError('agentId'));
    return getApiClient()
      .get<WidgetVersionResponse[]>(`/widgets/versions/by_agent/${cleanAgentId}`)
      .then((res) => res.data)
      .catch((err) => {
        if ((err as any).response?.status === 404) return [];
        return handleApiError(err, `listing versions for agent ${cleanAgentId}`);
      });
  },

  getVersion: (versionId: UUID): ServiceResponse<WidgetVersionResponse> => {
    const cleanId = normalizeUuid(versionId);
    return getApiClient()
      .get<WidgetVersionResponse>(`/widgets/versions/${cleanId}`)
      .then((res) => res.data)
      .catch((err) => handleApiError(err, `fetching version ${cleanId}`));
  },

  createVersion: (
    payload: WidgetVersionCreatePayload
  ): ServiceResponse<WidgetVersionResponse> => {
    return getApiClient()
      .post<WidgetVersionResponse>('/widgets/versions', payload)
      .then((res) => res.data)
      .catch((err) => handleApiError(err, 'creating widget version'));
  },

  updateVersion: (
    versionId: UUID,
    payload: WidgetVersionUpdatePayload
  ): ServiceResponse<WidgetVersionResponse> => {
    const cleanId = normalizeUuid(versionId);
    if (!payload || Object.keys(payload).length === 0) {
      // If no payload is provided, just refetch
      return widgetService.getVersion(cleanId as UUID);
    }
    return getApiClient()
      .put<WidgetVersionResponse>(`/widgets/versions/${cleanId}`, payload)
      .then((res) => res.data)
      .catch((err) => handleApiError(err, `updating version ${cleanId}`));
  },

  deleteVersion: (versionId: UUID): ServiceResponse<{ status: 'success' }> => {
    const cleanId = normalizeUuid(versionId);
    return getApiClient()
      .delete(`/widgets/versions/${cleanId}`)
      .then(() => ({ status: 'success' } as const))
      .catch((err) => handleApiError(err, `deleting version ${cleanId}`));
  },

  activateVersion: (versionId: UUID): ServiceResponse<WidgetVersionResponse> => {
    const cleanId = normalizeUuid(versionId);
    return getApiClient()
      .post<WidgetVersionResponse>(`/widgets/versions/${cleanId}/activate`)
      .then((res) => res.data)
      .catch((err) => handleApiError(err, `activating version ${cleanId}`));
  },

  getTemplateEmbedCode: (templateId: UUID): ServiceResponse<WidgetEmbedCodeResponse> => {
    const cleanId = normalizeUuid(templateId);
    if (!cleanId) return Promise.resolve(buildMissingIdError('templateId'));
    return getApiClient()
      .get<WidgetEmbedCodeResponse>(`/widgets/templates/${cleanId}/embed-code`)
      .then((res) => res.data)
      .catch((err) =>
        handleApiError(err, `fetching embed code for template ${cleanId}`)
      );
  },

  getEmbedCode: (versionId: UUID): ServiceResponse<WidgetEmbedCodeResponse> => {
    const cleanId = normalizeUuid(versionId);
    if (!cleanId) return Promise.resolve(buildMissingIdError('versionId'));
    return getApiClient()
      .get<WidgetEmbedCodeResponse>(`/widgets/versions/${cleanId}/embed-code`)
      .then((res) => res.data)
      .catch((err) =>
        handleApiError(err, `fetching embed code for version ${cleanId}`)
      );
  },

  // ---------------------------------------------------------------------------
  // Status + Schema
  // ---------------------------------------------------------------------------
  getTemplateConfigSchema: (
    templateId: UUID
  ): ServiceResponse<WidgetTemplateConfigSchemaResponse> => {
    const cleanId = normalizeUuid(templateId);
    return getApiClient()
      .get<WidgetTemplateConfigSchemaResponse>(`/widgets/templates/${cleanId}/config-schema`)
      .then((res) => res.data)
      .catch((err) =>
        handleApiError(err, `fetching config schema for template ${cleanId}`)
      );
  },

  updateTemplateConfigSchema: (
    templateId: UUID,
    payload: WidgetTemplateConfigSchemaUpdatePayload
  ): ServiceResponse<WidgetTemplateConfigSchemaResponse> => {
    const cleanId = normalizeUuid(templateId);
    return getApiClient()
      .put<WidgetTemplateConfigSchemaResponse>(`/widgets/templates/${cleanId}/config-schema`, payload)
      .then((res) => res.data)
      .catch((err) =>
        handleApiError(err, `updating config schema for template ${cleanId}`)
      );
  },

  getVersionStatus: (
    versionId: UUID
  ): ServiceResponse<WidgetVersionStatusResponse> => {
    const cleanId = normalizeUuid(versionId);
    return getApiClient()
      .get<WidgetVersionStatusResponse>(`/widgets/versions/${cleanId}/status`)
      .then((res) => res.data)
      .catch((err) =>
        handleApiError(err, `fetching status for version ${cleanId}`)
      );
  },

  // ---------------------------------------------------------------------------
  // Previews
  // ---------------------------------------------------------------------------
  createPreviewToken: (
    versionId: UUID,
    ttlSeconds = 1800
  ): ServiceResponse<WidgetPreviewTokenResponse> => {
    const cleanId = normalizeUuid(versionId);
    return getApiClient()
      .post<WidgetPreviewTokenResponse>(
        `/widgets/versions/${cleanId}/previews`,
        null,
        { params: { ttl_seconds: ttlSeconds } }
      )
      .then((res) => res.data)
      .catch((err) =>
        handleApiError(err, `creating preview token for version ${cleanId}`)
      );
  },

  getPreviewScript: (token: string, overrides?: Record<string, any>): string => {
    const base = getRawApiBaseUrl();
    let url = `${base}/api/v1/widgets/previews/${encodeURIComponent(token)}/serve.js`;
    if (overrides && Object.keys(overrides).length) {
      const json = JSON.stringify(overrides);
      const b64url = btoa(json).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
      url += `?overrides=${b64url}`;
    }
    return `<script src="${url}" async defer></script>`;
  },

  // ---------------------------------------------------------------------------
  // WAT (Widget Access Token) helpers
  // NOTE: BE returns { token, ttl }. If you rely on expires_utc,
  // map it in your calling code or introduce a dedicated WatResponse type.
  // ---------------------------------------------------------------------------
  issueWat: async (
    versionId: UUID,
    apiKey: string,
    payload?: { domain?: string | null }
  ): ServiceResponse<WidgetPreviewTokenResponse> => {
    const cleanId = normalizeUuid(versionId);
    if (!cleanId) {
      return Promise.resolve(
        handleApiError(new Error('Version ID required.'), 'issuing WAT')
      );
    }
    if (!apiKey) {
      return Promise.resolve(
        handleApiError(new Error('API Key required.'), 'issuing WAT')
      );
    }

    const rawBaseUrl = getRawApiBaseUrl();
    const url = `${rawBaseUrl}/api/v1/widgets/versions/${cleanId}/issue-wat`;
    try {
      const { data } = await axios.post<WidgetPreviewTokenResponse>(url, payload || {}, {
        headers: { Authorization: `Bearer ${apiKey}`, 'Content-Type': 'application/json' },
      });
      return data;
    } catch (err) {
      return handleApiError(err, `issuing WAT for version ${cleanId}`);
    }
  },

  refreshWat: async (
    versionId: UUID,
    oldWatToken: string
  ): ServiceResponse<WidgetPreviewTokenResponse> => {
    const cleanId = normalizeUuid(versionId);
    if (!cleanId) {
      return Promise.resolve(
        handleApiError(new Error('Version ID required.'), 'refreshing WAT')
      );
    }
    if (!oldWatToken) {
      return Promise.resolve(
        handleApiError(new Error('Old WAT required.'), 'refreshing WAT')
      );
    }

    const rawBaseUrl = getRawApiBaseUrl();
    const url = `${rawBaseUrl}/api/v1/widgets/versions/${cleanId}/refresh-wat`;
    try {
      const { data } = await axios.post<WidgetPreviewTokenResponse>(url, {}, {
        headers: { Authorization: `Bearer ${oldWatToken}`, 'Content-Type': 'application/json' },
      });
      return data;
    } catch (err) {
      return handleApiError(err, `refreshing WAT for version ${cleanId}`);
    }
  },

  // ---------------------------------------------------------------------------
  // Config patching
  // ---------------------------------------------------------------------------
  patchConfig: (
    versionId: UUID,
    configPatch: Record<string, any>
  ): ServiceResponse<{ id: string; config: Record<string, any> }> => {
    const cleanId = normalizeUuid(versionId);
    return getApiClient()
      .patch<{ id: string; config: Record<string, any> }>(
        `/widgets/versions/${cleanId}/config`,
        configPatch
      )
      .then((res) => res.data)
      .catch((err) =>
        handleApiError(err, `patching config for version ${cleanId}`)
      );
  },

  updateConfigSection: async (
    versionId: UUID,
    sectionKey: string,
    partial: Record<string, any>
  ): ServiceResponse<{ id: string; config: Record<string, any> }> => {
    // Fetch current config, shallow merge the section, and patch
    const current = await widgetService.getVersion(versionId);
    if ((current as any).isError) return current as any;

    const baseConfig = (current as any).config || {};
    const existingSection = baseConfig[sectionKey] || {};
    const newSection = { ...existingSection, ...partial };

    return widgetService.patchConfig(versionId, { [sectionKey]: newSection });
  },
};
